import { useState, useEffect, useCallback } from "react";
import { useUser, useClerk, useAuth } from "@clerk/clerk-react";
import { DashboardLayout } from "./Dashboard";
import { cn } from "@/lib/utils";
import { getUserMe, updateUserSettings, ApiError } from "@/lib/api";
import {
  User,
  Mail,
  Shield,
  LogOut,
  Trash2,
  Check,
  AlertTriangle,
  X,
  Loader2,
  Save,
  Building,
  Globe,
  CheckCircle
} from "lucide-react";

export function SettingsPage() {
  const { user: clerkUser } = useUser();
  const { signOut } = useClerk();
  const { getToken } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // API user data
  const [apiUser, setApiUser] = useState<{
    id: string;
    email: string;
    name: string | null;
    company?: string | null;
    timezone?: string;
    plan: string;
    created_at: string;
  } | null>(null);

  // Form state
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [timezone, setTimezone] = useState("UTC");
  const [isSaving, setIsSaving] = useState(false);

  // Track if form has changes
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch user data
  const fetchUser = useCallback(async () => {
    try {
      setError(null);
      const data = await getUserMe(getToken);
      setApiUser(data.user);
      setName(data.user.name || clerkUser?.fullName || "");
      setCompany(data.user.company || "");
      setTimezone(data.user.timezone || "UTC");
    } catch (err) {
      console.error("Failed to fetch user:", err);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to load settings. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  }, [getToken, clerkUser?.fullName]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Check for changes
  useEffect(() => {
    if (apiUser) {
      const nameChanged = name !== (apiUser.name || "");
      const companyChanged = company !== (apiUser.company || "");
      const timezoneChanged = timezone !== (apiUser.timezone || "UTC");
      setHasChanges(nameChanged || companyChanged || timezoneChanged);
    }
  }, [name, company, timezone, apiUser]);

  const handleSaveProfile = async () => {
    if (!hasChanges) return;

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await updateUserSettings(getToken, {
        name: name || undefined,
        company: company || undefined,
        timezone: timezone || undefined,
      });

      setApiUser(result.user);
      setSuccessMessage("Settings saved successfully!");
      setHasChanges(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Failed to save settings:", err);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to save settings. Please try again.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSignOut = () => {
    signOut({ redirectUrl: "/" });
  };

  const timezones = [
    "UTC",
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "America/Anchorage",
    "Pacific/Honolulu",
    "Europe/London",
    "Europe/Paris",
    "Europe/Berlin",
    "Asia/Tokyo",
    "Asia/Shanghai",
    "Asia/Singapore",
    "Australia/Sydney",
  ];

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-sky-500 animate-spin mx-auto mb-3" />
            <p className="text-slate-500">Loading settings...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <header className="bg-transparent px-8 py-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 mb-1">Settings</h1>
            <p className="text-slate-500">
              Manage your account and preferences
            </p>
          </div>
        </div>
      </header>

      <div className="px-8 pb-12">
        <div className="max-w-3xl space-y-8">
          {/* Error Alert */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <div className="p-1 bg-red-100 rounded-full text-red-600">
                <AlertTriangle className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-red-900">Error</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-500 hover:text-red-700 hover:bg-red-100 p-1 rounded-full transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Success Alert */}
          {successMessage && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-3">
              <div className="p-1 bg-emerald-100 rounded-full text-emerald-600">
                <CheckCircle className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-emerald-900">{successMessage}</p>
              </div>
              <button
                onClick={() => setSuccessMessage(null)}
                className="text-emerald-500 hover:text-emerald-700 hover:bg-emerald-100 p-1 rounded-full transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Account Info */}
          {apiUser && (
            <div className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.02)] overflow-hidden">
              <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-2">
                <User className="w-5 h-5 text-slate-400" />
                <h3 className="font-semibold text-slate-800">Account</h3>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-sky-400 to-sky-600 rounded-full flex items-center justify-center text-white text-2xl font-semibold shadow-inner border-2 border-white ring-2 ring-sky-100">
                      {clerkUser?.firstName?.[0] ||
                        apiUser.email?.[0]?.toUpperCase() ||
                        "U"}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-lg">
                        {apiUser.name || clerkUser?.fullName || "User"}
                      </p>
                      <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5">
                        <Mail className="w-3.5 h-3.5" />
                        {apiUser.email}
                      </p>
                      <p className="text-xs text-slate-400 mt-2 flex items-center gap-1.5">
                        <CheckCircle className="w-3 h-3 text-emerald-500" />
                        Member since {formatDate(apiUser.created_at)}
                      </p>
                    </div>
                  </div>
                  <span
                    className={cn(
                      "px-3 py-1 text-sm font-medium rounded-full capitalize border",
                      apiUser.plan === "free"
                        ? "bg-slate-50 text-slate-600 border-slate-200"
                        : apiUser.plan === "developer"
                          ? "bg-blue-50 text-blue-700 border-blue-200"
                          : apiUser.plan === "pro"
                            ? "bg-purple-50 text-purple-700 border-purple-200"
                            : apiUser.plan === "scale"
                              ? "bg-amber-50 text-amber-700 border-amber-200"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200",
                    )}
                  >
                    {apiUser.plan} Plan
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Profile Section */}
          <div className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.02)] overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-2">
              <Building className="w-5 h-5 text-slate-400" />
              <h3 className="font-semibold text-slate-800">Profile Details</h3>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Full Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Company
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      placeholder="Optional"
                      className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Timezone
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent bg-white transition-all appearance-none"
                  >
                    {timezones.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz.replace(/_/g, " ")}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 pt-2">
                <button
                  onClick={handleSaveProfile}
                  disabled={isSaving || !hasChanges}
                  className={cn(
                    "px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2",
                    hasChanges
                      ? "bg-slate-900 text-white hover:bg-slate-800 shadow-sm"
                      : "bg-slate-100 text-slate-400 cursor-not-allowed",
                  )}
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
                {hasChanges && (
                  <span className="text-sm text-slate-500 animate-in fade-in slide-in-from-left-2">
                    You have unsaved changes
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Email (Read-only, managed by Clerk) */}
          <div className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.02)] overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-2">
              <Mail className="w-5 h-5 text-slate-400" />
              <h3 className="font-semibold text-slate-800">Email Address</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-600 mb-3">
                Your email address is managed through your login provider.
              </p>
              <div className="flex items-center gap-3">
                <code className="px-3 py-2 bg-slate-50 rounded-lg text-sm font-mono text-slate-700 border border-slate-100">
                  {apiUser?.email ||
                    clerkUser?.primaryEmailAddress?.emailAddress}
                </code>
                <span className="text-xs text-slate-400 flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  Managed by authentication provider
                </span>
              </div>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="bg-white rounded-xl border border-red-200 shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-red-100 bg-red-50/30 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <h3 className="font-semibold text-red-900">Danger Zone</h3>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium text-slate-900">Sign out</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Sign out of your account on this device
                  </p>
                </div>
                <button
                  onClick={handleSignOut}
                  className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 hover:text-slate-900 transition-colors flex items-center gap-2 shadow-sm"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
              <div className="border-t border-slate-100 pt-6 flex justify-between items-center">
                <div>
                  <p className="font-medium text-slate-900">Delete account</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Permanently delete your account and all data
                  </p>
                </div>
                <button
                  className="px-4 py-2 bg-red-50 border border-red-100 text-red-600 text-sm font-medium rounded-lg hover:bg-red-100 hover:text-red-700 transition-colors flex items-center gap-2"
                  onClick={() =>
                    alert(
                      "Please contact support@weatherapi.dev to delete your account.",
                    )
                  }
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default SettingsPage;
